import React from 'react';
import { format, isAfter, parseISO } from 'date-fns';
import { uniqueId } from 'lodash-es';
import { TRACKED_ITEM_STATUSES } from '../constants';
import { formatRespondedDateTime } from './helpers';

const { NEEDED_FROM_OTHERS, NEEDED_FROM_YOU } = TRACKED_ITEM_STATUSES;

export const isFirstPartyRequest = (item, closedTrackedItemsEnabled) =>
  closedTrackedItemsEnabled
    ? item?.isFirstParty
    : item?.status === NEEDED_FROM_YOU;

export const isThirdPartyRequest = (item, closedTrackedItemsEnabled) =>
  closedTrackedItemsEnabled
    ? !item?.isFirstParty
    : item?.status === NEEDED_FROM_OTHERS;

export const hasSupportingDocuments = item => !!item?.documents?.length;

export const hasSuspenseDatePassed = item =>
  isAfter(new Date(), parseISO(item?.suspenseDate));

/**
 * A first-party tracked item shows the responded or past due request page
 * when either of these is true. Otherwise it shows the first-party request
 * page, even when it's closed
 *
 * 1. It has documents associated with it
 *
 * 2. Its suspense date has passed
 */
export const showOriginalRequestPage = item =>
  hasSupportingDocuments(item) || hasSuspenseDatePassed(item);

// The most recently uploaded document: the latest `uploadDate`, with
// `uploadDateTime` breaking ties on the same day
export const getNewestDocument = item =>
  [...(item?.documents || [])]
    .sort(
      (a, b) =>
        (a.uploadDate || '').localeCompare(b.uploadDate || '') ||
        (a.uploadDateTime || '').localeCompare(b.uploadDateTime || ''),
    )
    .pop();

/**
 * The aria-label for a first-party item's "We closed a request" link, and for
 * every third-party item's link
 *
 * @param {Object} item
 * @param {String} displayName
 * @returns {String}
 */
export const getRequestedDateLinkLabel = (item, displayName) =>
  // The API allows a null requestedDate
  item.requestedDate
    ? `Review ${displayName} from ${format(
        parseISO(item.requestedDate),
        'MMMM d, yyyy',
      )}`
    : `Review ${displayName}`;

/**
 * The aria-label for a first-party item's "We received your files" link
 *
 * @param {Object} item
 * @param {String} displayName
 * @returns {String}
 */
export const getFirstPartyRequestPageLinkLabel = (item, displayName) => {
  const label = `Review original ${displayName} request you responded to`;
  const { uploadDate, uploadDateTime } = getNewestDocument(item) || {};
  const respondedDate =
    (uploadDateTime && formatRespondedDateTime(uploadDateTime)) ||
    (uploadDate && format(parseISO(uploadDate), 'MMMM d, yyyy'));

  return respondedDate ? `${label} on ${respondedDate}` : label;
};

/**
 * A tracked item's "Review the request" link goes to the original page for the
 * tracked item. We construct that link here
 *
 * @param {Object} claim
 * @param {Object} item
 * @returns {Object} the item, with `requestPageLink` when it's third-party, or when
 * it's first-party, closed or has documents, and shows the original request page
 */
export const addRequestPageLink = (claim, item) => {
  const isFirstParty = isFirstPartyRequest(item, true);

  if (
    isFirstParty &&
    ((!item?.closedDate && !hasSupportingDocuments(item)) ||
      !showOriginalRequestPage(item))
  ) {
    return item;
  }

  const route = isFirstParty ? 'needed-from-you' : 'needed-from-others';

  return {
    ...item,
    requestPageLink: {
      href: `/your-claims/${claim.id}/${route}/${item.id}`,
    },
  };
};

/**
 * Add a tracked item to an existing array of tracked items
 *
 * @param {String} date
 * @param {String} description
 * @param {Object} item
 * @param {Array} items
 * @param {Object} [options]
 * @param {String} [options.displayDate] shown instead of the formatted `date`
 * @param {Boolean} [options.hideDate] `date` is only used for sorting
 */
const addItems = (
  date,
  description,
  item,
  items,
  { displayDate, hideDate } = {},
) => {
  const trackedItem = {
    id: `${item.id}-${uniqueId()}`,
    date,
    description,
    oldDisplayName: item.displayName,
    displayName: item.friendlyName || item.displayName,
    activityDescription: item.activityDescription,
    status: item.status,
    type: 'tracked_item',
    isFirstParty: item?.isFirstParty ?? false,
  };

  if (item?.requestPageLink) {
    trackedItem.requestPageLink = item.requestPageLink;
  }

  if (displayDate) {
    trackedItem.displayDate = displayDate;
  }

  if (hideDate) {
    trackedItem.hideDate = true;
  }

  items.push(trackedItem);
};

/**
 * Main entry function for collecting tracked items for a claim.
 * Adds request page links to tracked items
 * and adds friendly language for tracked items to indicate status
 *
 * @param {Object} claim
 * @param {Boolean} closedTrackedItemsEnabled
 * @param {Object} TrackedItem
 * @returns {Array} Array of tracked items
 */
export const generateTrackedItems = (
  claim,
  closedTrackedItemsEnabled,
  TrackedItem,
) => {
  const { trackedItems = [] } = claim.attributes;
  const items = [];

  const getActivityItem = (item, updatedDisplayName) => {
    if (!closedTrackedItemsEnabled) {
      return item;
    }

    const activityItem = addRequestPageLink(claim, item);

    if (activityItem.requestPageLink && isThirdPartyRequest(item, true)) {
      activityItem.requestPageLink.label = getRequestedDateLinkLabel(
        item,
        updatedDisplayName,
      );
    }

    return activityItem;
  };

  // A first-party item's closed and files received entries have different labels
  const withRequestPageLinkLabel = (activityItem, label) =>
    activityItem.requestPageLink
      ? {
          ...activityItem,
          requestPageLink: { ...activityItem.requestPageLink, label },
        }
      : activityItem;

  const handleDocumentsReceived = (item, displayName, activityItem) => {
    if (!closedTrackedItemsEnabled) {
      addItems(
        TrackedItem.getOldestDocumentDate(item),
        `We received your document(s) for the request: “${displayName}”`,
        item,
        items,
      );
      return;
    }

    const description = (
      <>
        <strong>We received your files for the request:</strong> “{displayName}
        .” You can still respond to it if you have more evidence to submit.
      </>
    );
    const { uploadDate, uploadDateTime } = getNewestDocument(item) || {};
    const displayDate =
      uploadDateTime && formatRespondedDateTime(uploadDateTime);

    if (displayDate) {
      addItems(uploadDateTime, description, activityItem, items, {
        displayDate,
      });
    } else if (uploadDate) {
      addItems(uploadDate, description, activityItem, items);
    } else {
      addItems(item.date, description, activityItem, items, {
        hideDate: true,
      });
    }
  };

  trackedItems.forEach(item => {
    const updatedDisplayName =
      (item.friendlyName && TrackedItem.getDisplayFriendlyName(item)) ||
      item.displayName;

    const displayName = TrackedItem.is5103Notice(item.displayName)
      ? 'List of evidence we may need (5103 notice)'
      : updatedDisplayName;

    const activityItem = getActivityItem(item, updatedDisplayName);
    // With the flag on, every entry for a third-party request links back to it
    const requestEntryItem =
      closedTrackedItemsEnabled && isThirdPartyRequest(item, true)
        ? activityItem
        : item;
    const isFirstPartyActivity =
      closedTrackedItemsEnabled && isFirstPartyRequest(item, true);
    const closedEntryItem = isFirstPartyActivity
      ? withRequestPageLinkLabel(
          activityItem,
          getRequestedDateLinkLabel(item, displayName),
        )
      : activityItem;
    const documentsEntryItem = isFirstPartyActivity
      ? withRequestPageLinkLabel(
          activityItem,
          getFirstPartyRequestPageLinkLabel(item, displayName),
        )
      : activityItem;

    if (item.closedDate) {
      addItems(
        item.closedDate,
        `We closed a request: “${displayName}”`,
        closedEntryItem,
        items,
      );
    }

    // With the flag on, first-party requests don't get a receivedDate entry
    if (
      item.receivedDate &&
      (!closedTrackedItemsEnabled || isThirdPartyRequest(item, true))
    ) {
      addItems(
        item.receivedDate,
        `We completed a review for the request: “${displayName}”`,
        requestEntryItem,
        items,
      );
    }

    if (hasSupportingDocuments(item)) {
      handleDocumentsReceived(item, displayName, documentsEntryItem);
    }

    if (item.requestedDate) {
      if (item.status === 'NEEDED_FROM_OTHERS') {
        addItems(
          item.requestedDate,
          TrackedItem.getIsDBQ(item)
            ? `We made a request: “${displayName}.”`
            : `We made a request outside the VA: “${displayName}.”`,
          requestEntryItem,
          items,
        );
      } else {
        addItems(
          item.requestedDate,
          `We opened a request: “${displayName}”`,
          requestEntryItem,
          items,
        );
      }
    }
  });

  return items;
};
